import React from "react";
import {Breadcrumb} from "antd";
import {Link, useLocation} from "react-router-dom";
import {observer} from "mobx-react";
import {useRequiredStore} from "../../../utils/store";
import {RootStoreContext} from "../../../stores/root/root.store";
import {useTranslate} from "../../../hooks/translate.hook";
import {matchPath} from "react-router";
import {RouteConfig, routers} from "../../../routers/routers";
import {decodePathname} from "../../../utils/route";

interface BreadcrumbConfig {
    systemName: string;
    path: string;
    fullWith: boolean;
}

export const Breadcrumbs: React.FC = observer(() => {
    const {pageStore} = useRequiredStore(RootStoreContext);
    const t = useTranslate();
    const location = useLocation();

    React.useEffect(() => {
        const configs = getAllCrumbConfigs(decodePathname(location.pathname), routers);
        const lastIndex = Object.keys(configs).pop();
        pageStore.setRoutes(configs);
        if (lastIndex) {
            pageStore.setFullWidth(configs[lastIndex].fullWith);
        }
    }, [location.pathname, pageStore]);

    return (
        <Breadcrumb separator="/" className={"breadcrumbs"}>
            <Breadcrumb.Item key="home">
                <Link to="/">{t("MAIN_PAGE")}</Link>
            </Breadcrumb.Item>
            {Object.keys(pageStore.routes).map(key => (
                <Breadcrumb.Item key={key}>
                    <Link to={pageStore.routes[key].path}>{pageStore.routes[key].systemName ? t(pageStore.routes[key].systemName) : ""}</Link>
                </Breadcrumb.Item>
            ))}
        </Breadcrumb>
    );
});

const getAllCrumbConfigs = (
    pathname: string,
    routes: RouteConfig[],
    data: Record<string, BreadcrumbConfig> = {}
): Record<string, BreadcrumbConfig> => {
    let partialMatchData: Record<string, BreadcrumbConfig> | null = null;

    for (const route of routes) {
        const match = matchPath({path: route.path, end: route.exact}, pathname);

        if (match) {
            const matchedData = {
                ...data,
                [route.name]: {
                    fullWith: route.fullWidth || false,
                    systemName: route.systemName,
                    path: match.pathnameBase,
                },
            };

            if (route.children) {
                const childData = getAllCrumbConfigs(pathname, route.children, matchedData);

                if (childData !== matchedData) {
                    return childData;
                }
            }

            if (match.pathnameBase === pathname) {
                return matchedData;
            }

            partialMatchData = matchedData;
        }
    }

    return partialMatchData || data;
};
